import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import TradeProgressGraph from "features/trades/common/TradeProgressGraph.tsx";
import {
  AnalyticsPageName,
  LanguageNameSpaces,
  TradeExtraInfoGeneralMapData,
  TradeGraphData,
} from "@shared/types";
import React, { useContext } from "react";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context.tsx";
import Title from "common/Typography/Title.tsx";
import Row from "antd/es/row";
import Col from "antd/es/col";
import styled from "styled-components";
import { typography } from "@shared/common/Themes/typography.ts";
import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import { HelperTooltipProps } from "common/Tooltips/HelperTooltip.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

interface GeneralTabProps {
  data: TradeExtraInfoGeneralMapData | undefined;
}
const GeneralTab = ({ data }: GeneralTabProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    generalData,
    isGridBotTrade,
    getTradeExtraInfoGeneralStatus,
    getTradeExtraInfoStatsStatus,
  } = useContext(TradeDetailsContext)!;

  const getSectionData = (
    sectionKeys: (keyof TradeExtraInfoGeneralMapData)[]
  ) => {
    return Object.entries(data || {})
      .filter(([key]) => sectionKeys.includes(key))
      .sort((a, b) => sectionKeys.indexOf(a[0]) - sectionKeys.indexOf(b[0]));
  };

  const getParsedValue = (
    key: keyof TradeExtraInfoGeneralMapData,
    value: string | number | null
  ) => {
    if (key === "Exchange") {
      return (
        <TextWithIcon
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={value?.toString()}
          text={value}
        />
      );
    }
    return value;
  };

  const keyToHelperData: Record<string, HelperTooltipProps> = {
    [POTENTIAL_PROFIT_KEY]: {
      analyticsPageName: AnalyticsPageName.TRADE_EXTRA_DETAILS,
      analyticsFieldName: POTENTIAL_PROFIT_KEY,
      description: t("general_tab.tooltips.potential_profit_description"),
      title: t("general_tab.tooltips.potential_profit_title"),
    },
  };

  const getDisplayedData = (section: GeneralTabSections) => {
    return (
      <div>
        <Title
          level={2}
          $weight={typography.fontWeights.SEMI_BOLD}
          style={{ marginBottom: 12 }}
        >
          {t(section)}
        </Title>
        <Row gutter={[16, 0]}>
          {getSectionData(GENERAL_TAB_SECTION_TO_KEYS[section]).map(
            ([name, value]) => (
              <Col span={12} key={name}>
                <ExtraInfoItem
                  key={name}
                  name={t(FIELD_NAME_TO_TRANSLATION_KEY[name])}
                  value={getParsedValue(name, value)}
                  titleHelperProps={keyToHelperData[name]}
                  style={{ flexDirection: "column", alignItems: "flex-start" }}
                  ellipsis={true}
                  autoHeight={true}
                  nameWrapperStyle={{ paddingBottom: 4 }}
                />
              </Col>
            )
          )}
        </Row>
      </div>
    );
  };

  const infoMap = (
    <>
      {Object.values(GeneralTabSections).map((section, index) => {
        const isLast = index === Object.values(GeneralTabSections).length - 1;
        return (
          <div key={index}>
            {getDisplayedData(section)}
            {!isLast && <Divider />}
          </div>
        );
      })}
    </>
  );

  const loading =
    getTradeExtraInfoGeneralStatus.isLoading ||
    getTradeExtraInfoStatsStatus.isLoading ||
    !data;

  return (
    <LoadingSpinnerOverlay
      isLoading={loading}
      spinnerSize="large"
      hideContentWhileLoading={true}
    >
      {!isGridBotTrade && (
        <TradeProgressGraph
          graphData={generalData?.data.graph_data as TradeGraphData}
          size="l"
          style={GRAPH_STYLE}
        />
      )}
      {infoMap}
    </LoadingSpinnerOverlay>
  );
};

export default GeneralTab;

const GRAPH_STYLE = { marginBottom: 28 };

enum GeneralTabSections {
  ACCOUNT_STATUS = "general_tab.sections.account_status",
  TRADE_DETAILS = "general_tab.sections.trade_details",
  PROFIT_RISK_METRICS = "general_tab.sections.profit_risk_metrics",
}

const POTENTIAL_PROFIT_KEY = "Potential Profit";

const FIELD_NAME_TO_TRANSLATION_KEY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  Account: "general_tab.fields.account",
  Exchange: "general_tab.fields.exchange",
  Status: "general_tab.fields.status",
  Source: "general_tab.fields.source",
  "Current Price": "general_tab.fields.current_price",
  "Opened at": "general_tab.fields.opened_at",
  "Entry Price": "general_tab.fields.entry_price",
  "Allocated Amount": "general_tab.fields.allocated_amount",
  "Filled Quantity": "general_tab.fields.filled_quantity",
  "Long/Short": "general_tab.fields.long_short",
  "Trade type": "general_tab.fields.trade_type",
  Leverage: "general_tab.fields.leverage",
  "Margin Type": "general_tab.fields.margin_type",
  "Estimated Liquidation Price":
    "general_tab.fields.estimated_liquidation_price",
  "BTC PnL": "general_tab.fields.btc_pnl",
  "USD PnL": "general_tab.fields.usd_pnl",
  "Risk/Reward": "general_tab.fields.risk_reward",
  [POTENTIAL_PROFIT_KEY]: "general_tab.fields.potential_profit",
};

const GENERAL_TAB_SECTION_TO_KEYS: Record<
  GeneralTabSections,
  (keyof TradeExtraInfoGeneralMapData)[]
> = {
  [GeneralTabSections.ACCOUNT_STATUS]: [
    "Account",
    "Exchange",
    "Status",
    "Source",
  ],
  [GeneralTabSections.TRADE_DETAILS]: [
    "Current Price",
    "Opened at",
    "Entry Price",
    "Allocated Amount",
    "Filled Quantity",
    "Long/Short",
    "Trade type",
    "Leverage",
    "Margin Type",
    "Estimated Liquidation Price",
  ],
  [GeneralTabSections.PROFIT_RISK_METRICS]: [
    "BTC PnL",
    "USD PnL",
    "Risk/Reward",
    POTENTIAL_PROFIT_KEY,
  ],
};

const Divider = styled.div`
  border-top: ${(p) => p.theme.borders.lightest};
  margin-top: 20px;
  padding-bottom: 20px;
`;
